import { useState, useEffect, useRef } from "react";
import {
  parsePhoneNumberFromString,
  getCountryCallingCode,
} from "libphonenumber-js";
import { PhoneField } from "../../components/PhoneField/PhoneField";
import { REST_API_URL } from "../../api/config";
import { useTranslation } from "react-i18next";
import { buildHeaders } from "../../api/buildHeaders";
import { saveToken } from "../../utils/localStorage";
import {
  FiPlus,
  FiUser,
  FiTrash2,
  FiChevronDown,
  FiCheck,
  FiSave,
  FiUsers,
  FiMail,
  FiGlobe,
} from "react-icons/fi";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch } from "react-redux";
import { LanguageModal } from "../../components/LanguageModal";
import { AILanguageModal } from "../../components/AILanguageModal";
import { CustomAlertModal } from "../../components/CustomAlertModal";
import { ModalPortal } from "../../components/ModalPortal";
import { FiChevronRight } from "react-icons/fi";
import { LocationSelector } from "../../components/LocationSelector";
import { getUserCurrencyThunk } from "../../redux/slices/profileSlice/asyncthunk";
const BASE_URL = REST_API_URL;
const USER_INFO_URL = `${BASE_URL}/auth/userinfo/`;

const fadeInUp = {
  initial: { opacity: 1, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 1, y: -20 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

// Helper functions for phone number handling
const getNationalNumber = (fullPhoneNumber) => {
  if (!fullPhoneNumber) return "";
  const parsed = parsePhoneNumberFromString(fullPhoneNumber);
  if (parsed) {
    return parsed.nationalNumber;
  }
  return fullPhoneNumber;
};

const getFullPhoneNumber = (nationalNumber, countryCode) => {
  if (!nationalNumber) return "";
  return `${countryCode}${nationalNumber}`;
};

function UserInformationCardSkeleton() {
  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-6 sm:p-8 flex flex-col gap-6 sm:gap-8 border border-gray-100 animate-pulse">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 bg-gray-200 rounded-lg shrink-0" />
        <div className="h-6 bg-gray-200 rounded-md w-48" />
      </div>

      {/* Two Column Layout */}
      <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">
        {/* Left Column */}
        <div className="flex flex-col gap-6 w-full lg:w-[50%]">
          {/* Full Name */}
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-24" />
            <div className="h-14 w-full bg-gray-100 rounded-xl border border-gray-200" />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-16" />
            <div className="h-14 w-full bg-gray-100 rounded-xl border border-gray-200" />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-32" />
            <div className="h-14 w-full bg-gray-100 rounded-xl border border-gray-200" />
          </div>

          {/* Currency */}
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-20" />
            <div className="h-14 w-full bg-gray-100 rounded-xl border border-gray-200" />
          </div>
        </div>

        {/* Right Column (Location / Map) */}
        <div className="flex flex-col gap-6 w-full lg:w-[50%]">
          {/* Address input */}
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-28" />
            <div className="h-14 w-full bg-gray-100 rounded-xl border border-gray-200" />
          </div>

          {/* Map */}
          <div className="space-y-2 flex-1 flex flex-col">
            <div className="h-4 bg-gray-200 rounded w-36" />
            <div className="h-48 sm:h-56 w-full bg-gray-200/80 rounded-2xl border border-gray-200" />
          </div>
        </div>
      </div>

      {/* Language Section */}
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-28" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="h-14 bg-gray-100 rounded-xl border border-gray-200" />
          <div className="h-14 bg-gray-100 rounded-xl border border-gray-200" />
        </div>
      </div>

      {/* Workplace Section */}
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-36" />
        <div className="flex gap-3">
          <div className="h-14 flex-1 bg-gray-100 rounded-xl border border-gray-200" />
          <div className="h-14 flex-1 bg-gray-100 rounded-xl border border-gray-200" />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-start pt-2">
        <div className="h-12 w-44 bg-gray-200 rounded-xl" />
      </div>
    </div>
  );
}

export function UserInformationCard() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [userType, setUserType] = useState("individual");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(""); // Now stores only national number
  const [addressDetails, setAddressDetails] = useState({
    country: "",
    city: "",
    address: "",
    state: "",
    postcode: "",
    fullAddress: "",
  });
  const [position, setPosition] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [companyName, setCompanyName] = useState("");

  const [workers, setWorkers] = useState([]);
  const [workPlaces, setWorkPlaces] = useState([]);
  const [selectedWorkPlaces, setSelectedWorkPlaces] = useState([]);
  const [allWorkplaces, setAllWorkplaces] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState("AM"); // Default to Armenia
  const [countryCode, setCountryCode] = useState("+374");
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const [currency, setCurrency] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [openCurrency, setOpenCurrency] = useState(false);
  const [loadingCurrency, setLoadingCurrency] = useState(true);

  const [showAddWorkersModal, setShowAddWorkersModal] = useState(false);
  const [workerEmails, setWorkerEmails] = useState([]);
  const [workerEmailInput, setWorkerEmailInput] = useState("");
  const [loadingAddWorkers, setLoadingAddWorkers] = useState(false);
  const [workersDropdownOpen, setWorkersDropdownOpen] = useState(false);
  const [notAvailableEmails, setNotAvailableEmails] = useState([]);

  const [workerToDelete, setWorkerToDelete] = useState(null);
  const [isDeletingWorker, setIsDeletingWorker] = useState(false);

  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showAILanguageModal, setShowAILanguageModal] = useState(false);
  const [aiLanguages, setAiLanguages] = useState([]);

  const [initialTimeZone, setInitialTimeZone] = useState("");
  const [timeZone, setTimeZone] = useState("");
  const [modalError, setModalError] = useState("");
  const [alertVisible, setAlertVisible] = useState(false);
  const [timeZoneAlertVisible, setTimeZoneAlertVisible] = useState(false);
  const isInitialLocation = useRef(true);
  const previousPosition = useRef(null);

  const currencyRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (currencyRef.current && !currencyRef.current.contains(event.target)) {
        setOpenCurrency(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [loading, setLoading] = useState(true);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    (async () => {
      if (isFirstLoad.current) {
        setLoading(true);
      }
      try {
        const res = await fetch(USER_INFO_URL, { headers: buildHeaders() });
        const data = await res.json();

        setUserType(data.user?.status || "individual");

        setFullName(
          `${data.user.first_name || ""} ${data.user.last_name || ""}`.trim(),
        );
        setEmail(data.user.email || "");

        // Fix: Store only national number without country code
        const nationalNumber = getNationalNumber(data.phone_number || "");
        setPhone(nationalNumber);

        // Detect country from phone number
        if (data.phone_number) {
          const parsed = parsePhoneNumberFromString(data.phone_number);
          if (parsed && parsed.country) {
            setSelectedCountry(parsed.country);
            setCountryCode("+" + getCountryCallingCode(parsed.country));
          }
        }

        setCompanyName(data.user?.company_name || "");

        let parsedPosition = null;
        if (data.address_url) {
          const match = data.address_url.match(/(?:query|q)=([^&]+)/);
          if (match) {
            const coords = match[1].split(",");
            if (coords.length >= 2) {
              const lat = parseFloat(coords[0]);
              const lng = parseFloat(coords[1]);
              if (!isNaN(lat) && !isNaN(lng)) {
                parsedPosition = { lat, lng };
              }
            }
          }
          if (!parsedPosition) {
            const pathMatch = data.address_url.match(
              /@(-?\d+\.\d+),(-?\d+\.\d+)/,
            );
            if (pathMatch) {
              const lat = parseFloat(pathMatch[1]);
              const lng = parseFloat(pathMatch[2]);
              if (!isNaN(lat) && !isNaN(lng)) {
                parsedPosition = { lat, lng };
              }
            }
          }
        }

        if (parsedPosition) {
          setPosition(parsedPosition);
        } else {
          const defaultPosition = { lat: 40.7128, lng: -74.006 };
          setPosition(defaultPosition);
        }

        if (data.currency) setCurrency(data.currency);

        if (Array.isArray(data.work_place)) {
          setWorkPlaces(data.work_place);
          setSelectedWorkPlaces(data.work_place.map((p) => p.id));
        }

        if (data.user?.status === "salon" && Array.isArray(data.workers)) {
          setWorkers(
            data.workers.filter((worker) => worker.email !== data.user.email),
          );
        }

        if (Array.isArray(data.user?.languages)) {
          setAiLanguages(data.user.languages);
        }

        setInitialTimeZone(data.time_zone || "");
        setTimeZone(data.time_zone || "");
        if (data.time_zone) {
          saveToken("time_zone", data.time_zone);
        }
      } catch (err) {
        console.error("Failed to load user info:", err);
      } finally {
        if (isFirstLoad.current) {
          isFirstLoad.current = false;
          setLoading(false);
        }
      }
    })();
  }, [showAILanguageModal, showLanguageModal]);

  useEffect(() => {
    (async () => {
      setLoadingCurrency(true);
      try {
        const res = await fetch(`${BASE_URL}/service/currencies/`, {
          headers: buildHeaders(),
        });

        const data = await res.json();

        setCurrencies(
          data.map((item) => ({
            code: item.short_name,
            name: item.short_name,
            flag: item.icon || "",
          })),
        );

        if (!currency && data.length > 0) setCurrency(data[0].short_name);
      } finally {
        setLoadingCurrency(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/auth/workplaces/`, {
          headers: buildHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setAllWorkplaces(data);
        }
      } catch (err) {
        console.error("Failed to load workplaces:", err);
      }
    })();
  }, []);

  useEffect(() => {
    if (
      !position ||
      typeof position.lat !== "number" ||
      typeof position.lng !== "number"
    ) {
      return;
    }

    if (isInitialLocation.current) {
      isInitialLocation.current = false;
      previousPosition.current = position;
      return;
    }

    if (
      previousPosition.current &&
      previousPosition.current.lat === position.lat &&
      previousPosition.current.lng === position.lng
    ) {
      return;
    }
    previousPosition.current = position;

    const checkTimezone = async () => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${position.lat}&longitude=${position.lng}&timezone=auto`,
        );
        if (res.ok) {
          const data = await res.json();
          const newTimezone = data?.timezone;
          const currentTimezone = initialTimeZone || timeZone;

          if (
            newTimezone &&
            currentTimezone &&
            newTimezone !== currentTimezone
          ) {
            setTimeZone(newTimezone);
            setTimeZoneAlertVisible(true);
          }
        }
      } catch (err) {
        console.error("Error checking timezone:", err);
      }
    };

    checkTimezone();
  }, [position, initialTimeZone, timeZone]);

  const save = async (overrideTimeZone) => {
    setIsSaving(true);
    if (!position) {
      toast.error(t("home.errorLocationRequired"));
      setIsSaving(false);
      return;
    }

    if (userType === "individual" && selectedWorkPlaces.length === 0) {
      toast.error(t("settings.selectAtLeastOneWorkplace"));
      setIsSaving(false);
      return;
    }

    if (phone && !isPhoneValid) {
      toast.error(t("invalidPhoneNumber"));
      setIsSaving(false);
      return;
    }

    // Fix: Build full phone number with country code for API
    const fullPhoneNumber = getFullPhoneNumber(phone, countryCode);

    const targetTimeZone =
      typeof overrideTimeZone === "string"
        ? overrideTimeZone
        : timeZone || initialTimeZone;

    const body = {
      full_name: fullName,
      company_name: userType === "salon" ? companyName : "",
      phone_number: fullPhoneNumber,
      address_url: `https://www.google.com/maps/search/?api=1&query=${position.lat},${position.lng}`,
      currency: currency,
      work_place_ids: userType === "individual" ? selectedWorkPlaces : [],
      new_time_zone: targetTimeZone,
    };

    try {
      const res = await fetch(USER_INFO_URL, {
        method: "PUT",
        headers: buildHeaders(),
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        let firstError = "";
        if (data && typeof data === "object") {
          const keys = Object.keys(data);
          if (keys.length > 0) {
            const val = data[keys[0]];
            firstError = Array.isArray(val)
              ? val.join(", ")
              : typeof val === "string"
                ? val
                : JSON.stringify(val);
          }
        }
        const errorText =
          firstError ||
          data?.detail ||
          data?.message ||
          t("settings.somethingWentWrong");
        setModalError(errorText);
        setAlertVisible(true);
        return;
      }

      toast.success(t("settings.profileUpdatedSuccessfully"));

      const refresh = await fetch(USER_INFO_URL, { headers: buildHeaders() });
      const freshData = await refresh.json();

      setFullName(
        freshData.user?.full_name ||
          `${freshData.user?.first_name || ""} ${
            freshData.user?.last_name || ""
          }`.trim(),
      );
      setEmail(freshData.user?.email || "");

      // Update phone with national number only
      const updatedNationalNumber = getNationalNumber(
        freshData.phone_number || "",
      );
      setPhone(updatedNationalNumber);

      setCompanyName(freshData.user?.company_name || "");
      setCurrency(freshData.currency || "");
      setInitialTimeZone(freshData.time_zone || "");
      setTimeZone(freshData.time_zone || "");
      if (freshData.time_zone) {
        saveToken("time_zone", freshData.time_zone);
      }

      if (Array.isArray(freshData.work_place)) {
        setWorkPlaces(freshData.work_place);
        setSelectedWorkPlaces(freshData.work_place.map((p) => p.id));
      }

      if (userType === "salon" && Array.isArray(freshData.workers)) {
        setWorkers(
          freshData.workers.filter(
            (worker) => worker.email !== freshData.user.email,
          ),
        );
      }
      dispatch(getUserCurrencyThunk());
      window.dispatchEvent(new Event("userUpdated"));
    } catch (err) {
      console.error(err);
      setModalError(err?.message || t("settings.somethingWentWrong"));
      setAlertVisible(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteWorker = async () => {
    if (!workerToDelete) return;
    setIsDeletingWorker(true);
    try {
      const res = await fetch(
        `${BASE_URL}/auth/delete-worker/${workerToDelete.id}/`,
        {
          method: "DELETE",
          headers: buildHeaders(),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.detail || data?.message || t("settings.failedToRemoveWorker"),
        );
      }
      setWorkers((prev) => prev.filter((w) => w.id !== workerToDelete.id));
      toast.success(t("settings.workerRemovedSuccessfully"));
      window.dispatchEvent(new Event("userUpdated"));
    } catch (error) {
      console.error(error);
      toast.error(error.message || t("settings.failedToRemoveWorker"));
    } finally {
      setIsDeletingWorker(false);
      setWorkerToDelete(null);
    }
  };

  const handleSaveWorkers = async () => {
    if (workerEmails.length === 0) {
      toast.error(t("settings.addAtLeastOneWorkerEmail"));
      return;
    }

    setLoadingAddWorkers(true);

    if (phone && !isPhoneValid) {
      toast.error(t("invalidPhoneNumber"));
      setLoadingAddWorkers(false);
      return;
    }

    try {
      const currentUserRes = await fetch(USER_INFO_URL, {
        headers: buildHeaders(),
      });
      const currentUserData = await currentUserRes.json();

      // Build full phone number with country code for API
      const fullPhoneNumber = getFullPhoneNumber(phone, countryCode);

      const body = {
        full_name: fullName,
        company_name: companyName || currentUserData.user?.company_name || "",
        phone_number: fullPhoneNumber || currentUserData.phone_number || "",
        address_url: position
          ?`https://www.google.com/maps/search/?api=1&query=${position.lat},${position.lng}`
          : currentUserData.address_url || "",
        business_description: currentUserData.business_description || "",
        currency: currency || currentUserData.currency || "",
        work_place_ids: selectedWorkPlaces,
        worker_emails: workerEmails,
      };

      const res = await fetch(USER_INFO_URL, {
        method: "PUT",
        headers: buildHeaders(),
        body: JSON.stringify(body),
      });

      const resData = await res.json();

      if (!res.ok) {
        throw new Error(
          resData?.detail ||
            resData?.message ||
            t("settings.failedToAddWorkers"),
        );
      }

      // Check which emails are not registered in the system
      const unavailable = Array.isArray(resData.not_available_emails)
        ? resData.not_available_emails
        : [];

      if (unavailable.length > 0) {
        setNotAvailableEmails(unavailable);
        // Remove invalid emails from the list so user can fix and retry
        setWorkerEmails((prev) => prev.filter((e) => !unavailable.includes(e)));
        toast.error(`${unavailable.length} ${t("settings.emailsNotFound")}`);
        // Keep modal open for correction
        return;
      }

      setNotAvailableEmails([]);

      const refreshRes = await fetch(USER_INFO_URL, {
        headers: buildHeaders(),
      });
      const freshData = await refreshRes.json();

      if (Array.isArray(freshData.workers)) {
        setWorkers(
          freshData.workers.filter(
            (worker) => worker.email !== freshData.user.email,
          ),
        );
      }

      if (freshData.user) {
        setFullName(
          `${freshData.user.first_name || ""} ${
            freshData.user.last_name || ""
          }`.trim(),
        );
        setCompanyName(freshData.user.company_name || "");
      }

      // Update phone with national number only
      const updatedNationalNumber = getNationalNumber(
        freshData.phone_number || "",
      );
      setPhone(updatedNationalNumber);

      setCurrency(freshData.currency || "");

      setShowAddWorkersModal(false);
      setWorkerEmails([]);
      setWorkerEmailInput("");

      toast.success(t("settings.workersInvitedSuccessfully"));
      window.dispatchEvent(new Event("userUpdated"));
    } catch (error) {
      console.error("Error adding workers:", error);
      toast.error(error.message || t("settings.failedToAddWorkers"));
    } finally {
      setLoadingAddWorkers(false);
    }
  };

  if (loading) {
    return <UserInformationCardSkeleton />;
  }

  return (
    <motion.div
      initial={{ opacity: 1, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-gradient-to-br from-white to-gray-50 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-4 sm:p-6 lg:p-8 flex flex-col gap-6 sm:gap-8 border border-gray-100"
    >
      <div className="flex items-center gap-3 mb-2 sm:mb-4">
        <div className="p-2 bg-gradient-to-br from-blue-500 to-[#020637] rounded-lg shrink-0">
          <FiUser className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg sm:text-xl font-medium sm:font-normal text-gray-800 truncate">
            {t("settings.userInformation")}
          </h2>
        </div>
      </div>

      {userType === "worker" ? (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-5 sm:space-y-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 w-full items-start">
            <motion.div
              variants={fadeInUp}
              className="space-y-1.5 sm:space-y-2"
            >
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide">
                {t("settings.fullName")}
              </label>
              <input
                className="h-12 sm:h-14 w-full px-3.5 sm:px-4 rounded-xl border border-gray-200 focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/20 focus:ring-opacity-50 transition-all duration-300 bg-white shadow-sm text-sm text-gray-800"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t("settings.enterFullName")}
              />
            </motion.div>

            <motion.div variants={fadeInUp}>
              <PhoneField
                phone={phone}
                country={selectedCountry}
                label={t("phoneNumber")}
                containerHeight="h-12 sm:h-14"
                required={false}
                onPhoneChange={(val, valid) => {
                  setPhone(val);
                  setIsPhoneValid(valid);
                }}
                onCountryChange={(c, code) => {
                  setSelectedCountry(c);
                  if (code) {
                    setCountryCode(code);
                  } else {
                    setCountryCode("+" + getCountryCallingCode(c));
                  }
                }}
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="space-y-1.5 sm:space-y-2 sm:col-span-2 lg:col-span-1"
            >
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide">
                {t("settings.languageSection")}
              </label>
              <button
                type="button"
                onClick={() => setShowLanguageModal(true)}
                className="h-12 sm:h-14 w-full flex items-center justify-between px-3.5 sm:px-4 bg-white rounded-xl border border-gray-200 hover:border-[#020637] hover:ring-2 hover:ring-[#020637]/15 shadow-sm transition-all duration-200 group cursor-pointer"
              >
                <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#020637]/5 flex items-center justify-center text-[#020637] group-hover:bg-[#020637]/14 transition-colors shrink-0">
                    <FiGlobe className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-gray-800 truncate">
                    {t("settings.mainLanguage")}
                  </span>
                </div>
                <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#0e1c7a] group-hover:translate-x-0.5 transition-all shrink-0 ml-2" />
              </button>
            </motion.div>
          </div>
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => save()}
            disabled={isSaving}
            className="mt-2 w-full h-12 sm:h-14 bg-gradient-to-r from-blue-600 to-[#020637] text-white rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            {isSaving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t("settings.saving")}
              </>
            ) : (
              <>
                <FiSave className="w-5 h-5" />
                {t("settings.saveChanges")}
              </>
            )}
          </motion.button>
        </motion.div>
      ) : (
        <>
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="flex flex-col gap-5 sm:gap-6 w-full lg:w-[50%] opacity-none"
            >
              <motion.div variants={fadeInUp} className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {t("settings.fullName")}
                </label>
                <input
                  className="h-14 w-full px-4 rounded-xl border border-gray-200 focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/20 focus:ring-opacity-50 transition-all duration-300 bg-white shadow-sm"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                />
              </motion.div>

              <motion.div variants={fadeInUp} className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {t("settings.email")}
                </label>
                <div className="relative">
                  <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    className="h-14 w-full pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 disabled"
                    value={email}
                    readOnly
                  />
                </div>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <PhoneField
                  phone={phone}
                  country={selectedCountry}
                  label={t("phoneNumber")}
                  containerHeight="h-12 sm:h-14"
                  required={false}
                  onPhoneChange={(val, valid) => {
                    setPhone(val);
                    setIsPhoneValid(valid);
                  }}
                  onCountryChange={(c, code) => {
                    setSelectedCountry(c);
                    if (code) {
                      setCountryCode(code);
                    } else {
                      setCountryCode("+" + getCountryCallingCode(c));
                    }
                  }}
                />
              </motion.div>

              <motion.div variants={fadeInUp} className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {t("settings.currency")}
                </label>
                <div ref={currencyRef} className="relative">
                  <div
                    className="h-14 border border-gray-200 rounded-xl px-4 w-full flex items-center justify-between cursor-pointer bg-white shadow-sm hover:shadow transition-all duration-200"
                    onClick={() => setOpenCurrency((v) => !v)}
                  >
                    <span className="flex items-center gap-3">
                      {!loadingCurrency &&
                        currencies.find((c) => c.code === currency)?.flag && (
                          <img
                            alt={currency}
                            src={
                              currencies.find((c) => c.code === currency)?.flag
                            }
                            className="w-6 h-6 object-contain rounded-full"
                          />
                        )}
                      <span className="text-sm font-medium text-gray-800">
                        {loadingCurrency
                          ? t("settings.loading")
                          : currency || t("settings.selectCurrency")}
                      </span>
                    </span>
                    <FiChevronDown
                      className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                        openCurrency ? "rotate-180" : ""
                      }`}
                    />
                  </div>

                  <AnimatePresence>
                    {openCurrency && (
                      <motion.div
                        initial={{ opacity: 1, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 1, y: -10 }}
                        className="absolute z-50 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-h-64 overflow-y-auto"
                      >
                        {currencies.map((item) => (
                          <motion.div
                            key={item.code}
                            whileHover={{
                              backgroundColor: "rgba(2, 6, 55, 0.05)",
                            }}
                            className="flex items-center justify-between px-4 py-3 cursor-pointer border-b border-gray-50 last:border-b-0"
                            onClick={() => {
                              setCurrency(item.code);
                              setOpenCurrency(false);
                            }}
                          >
                            <span className="flex items-center gap-3">
                              {item.flag && (
                                <img
                                  src={item.flag}
                                  alt={item.name}
                                  className="w-6 h-6 object-contain rounded-full"
                                />
                              )}
                              <span className="text-sm font-medium text-gray-800">
                                {item.name}
                              </span>
                            </span>

                            {currency === item.code && (
                              <div className="w-6 h-6 rounded-full bg-[#020637]/10 flex items-center justify-center">
                                <FiCheck className="w-3 h-3 text-[#020637]" />
                              </div>
                            )}
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>

              {userType === "salon" && (
                <motion.div variants={fadeInUp} className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {t("settings.companyName")}
                  </label>
                  <input
                    className="h-14 w-full px-4 rounded-xl border border-gray-200 focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/20 focus:ring-opacity-50 transition-all duration-300 bg-white shadow-sm"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={t("settings.enterCompanyName")}
                  />
                </motion.div>
              )}

              {userType === "salon" && (
                <motion.div
                  variants={fadeInUp}
                  className="space-y-3 rounded-xl"
                >
                  <label className="font-semibold text-sm text-gray-700 uppercase tracking-wide">
                    {t("settings.workers")}
                  </label>

                  {workers.length === 0 ? (
                    <>
                      <div className="h-12 border-2 border-dashed border-gray-300 rounded-xl px-4 flex items-center justify-center bg-gray-50">
                        <span className="text-gray-400 italic">
                          {t("settings.noWorkersYet")}
                        </span>
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="h-12 bg-gradient-to-r from-blue-600 to-[#020637] text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
                        onClick={() => setShowAddWorkersModal(true)}
                      >
                        <FiPlus className="w-5 h-5" />
                        {t("settings.addWorker")}
                      </motion.button>
                    </>
                  ) : (
                    <>
                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        onClick={() => setWorkersDropdownOpen((prev) => !prev)}
                        className="h-12 border border-gray-200 rounded-xl px-4 flex items-center justify-between cursor-pointer bg-white shadow-sm hover:shadow transition-all duration-200"
                      >
                        <span className="text-gray-800 font-medium">
                          {t("settings.viewWorkers")}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 bg-[#020637]/10 text-[#020637] rounded-full text-sm font-semibold">
                            {workers.length}
                          </span>
                          <FiChevronDown
                            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                              workersDropdownOpen ? "rotate-180" : ""
                            }`}
                          />
                        </div>
                      </motion.div>

                      <AnimatePresence>
                        {workersDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 1, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 1, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="bg-white border rounded-xl shadow-lg overflow-hidden"
                          >
                            <div className="max-h-48 overflow-y-auto">
                              {workers.map((w) => {
                                const avatar =
                                  w.image_url ||
                                  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

                                const registered = Boolean(w.full_name);

                                return (
                                  <motion.div
                                    key={w.id}
                                    whileHover={{
                                      backgroundColor: "rgba(2, 6, 55, 0.05)",
                                    }}
                                    className="flex items-center gap-3 px-3 py-3 border-b border-gray-100 last:border-b-0"
                                  >
                                    <div className="relative">
                                      <img
                                        src={avatar}
                                        alt="avatar"
                                        className="w-10 h-10 rounded-full object-cover border-2 border-white shadow"
                                      />
                                      {registered && (
                                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                                      )}
                                    </div>

                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-800">
                                        {registered
                                          ? w.full_name
                                          : t("settings.notRegistered")}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {w.email || w.phone_number || ""}
                                      </div>
                                    </div>

                                    <div
                                      className={`px-2 py-1 rounded text-xs font-medium ${
                                        registered
                                          ? "bg-green-100 text-green-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                                    >
                                      {registered
                                        ? t("settings.active")
                                        : t("settings.pending")}
                                    </div>

                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setWorkerToDelete(w);
                                      }}
                                      className="p-1.5 hover:bg-red-50 rounded-lg transition-colors ml-1"
                                      title="Remove worker"
                                    >
                                      <FiTrash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                                    </motion.button>
                                  </motion.div>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="p-2 h-12 bg-gradient-to-r from-blue-600 to-[#020637] text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
                        onClick={() => setShowAddWorkersModal(true)}
                      >
                        <FiPlus className="w-5 h-5" />
                        {t("settings.addMoreWorkers")}
                      </motion.button>
                    </>
                  )}
                </motion.div>
              )}
            </motion.div>

            <LocationSelector
              position={position}
              setPosition={setPosition}
              setAddressDetails={setAddressDetails}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
              {t("settings.languageSection")}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setShowLanguageModal(true)}
                className="flex items-center justify-between px-4 py-3.5 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-3">
                  <FiGlobe className="w-5 h-5 text-gray-400 group-hover:text-[#0e1c7a] transition-colors" />
                  <span className="text-sm font-medium text-gray-800">
                    {t("settings.mainLanguage")}
                  </span>
                </div>
                <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                type="button"
                onClick={() => setShowAILanguageModal(true)}
                className="flex items-center justify-between px-4 py-3.5 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-3">
                  <span className="text-base leading-none group-hover:scale-110 transition-transform">
                    🤖
                  </span>
                  <span className="text-sm font-medium text-gray-800">
                    {t("settings.aiLanguage")}
                  </span>
                </div>
                <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>

          {userType === "individual" && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                {t("settings.whereDoYouWork")}
              </label>
              <div className="flex gap-3">
                {allWorkplaces.map((item) => {
                  const isSelected = selectedWorkPlaces.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedWorkPlaces(
                            selectedWorkPlaces.filter((id) => id !== item.id),
                          );
                        } else {
                          setSelectedWorkPlaces([
                            ...selectedWorkPlaces,
                            item.id,
                          ]);
                        }
                      }}
                      className={`flex flex-1 items-center gap-3 px-4 py-3.5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? "border-[#020637] bg-[#020637]/5"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors shrink-0 ${
                          isSelected
                            ? "border-[#020637] bg-[#020637] text-white"
                            : "border-gray-300 bg-white"
                        }`}
                      >
                        {isSelected && (
                          <FiCheck className="w-3.5 h-3.5 stroke-[3] text-white" />
                        )}
                      </div>
                      <span
                        className={`text-sm font-medium ${isSelected ? "text-[#020637]" : "text-gray-700"}`}
                      >
                        {item.place_title}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => save()}
            disabled={isSaving}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-[#020637] text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                />
                <motion.span
                  initial={{ opacity: 0.8 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    repeat: Infinity,
                    duration: 1,
                    repeatType: "reverse",
                  }}
                >
                  {t("settings.savingChanges")}
                </motion.span>
              </>
            ) : (
              <>
                <motion.div
                  initial={{ rotate: 0 }}
                  whileHover={{ rotate: 15 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <FiSave className="w-6 h-6" />
                </motion.div>
                <span className="font-bold tracking-wide">
                  {t("settings.saveAllChanges")}
                </span>
              </>
            )}
          </motion.button>
        </>
      )}

      <ModalPortal>
      <AnimatePresence>
        {workerToDelete && (
          <motion.div
            key="delete-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4"
            onClick={() => !isDeletingWorker && setWorkerToDelete(null)}
          >
            <motion.div
              key="delete-modal"
              initial={{ opacity: 1, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 1, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mx-auto mb-4">
                  <FiTrash2 className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                  {t("settings.removeWorker")}
                </h3>
                <p className="text-sm text-gray-500 text-center">
                  {t("settings.removeWorkerConfirm", {
                    name: workerToDelete.full_name || workerToDelete.email,
                  })}
                </p>
              </div>
              <div className="border-t border-gray-200 p-4 bg-gray-50 flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setWorkerToDelete(null)}
                  disabled={isDeletingWorker}
                  className="flex-1 h-11 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  {t("settings.cancel")}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDeleteWorker}
                  disabled={isDeletingWorker}
                  className="flex-1 h-11 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeletingWorker ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t("settings.removing")}
                    </>
                  ) : (
                    <>
                      <FiTrash2 className="w-4 h-4" />
                      {t("settings.remove")}
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </ModalPortal>

      <ModalPortal>
      <AnimatePresence>
        {showAddWorkersModal && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4"
            onClick={() => {
              setShowAddWorkersModal(false);
              setNotAvailableEmails([]);
              setWorkerEmailInput("");
            }}
          >
            <motion.div
              key="modal"
              initial={{ opacity: 1, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 1, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {t("settings.addTeamMembers")}
                    </h3>
                    <p className="text-gray-500 text-sm mt-1">
                      {t("settings.inviteWorkers")}
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setShowAddWorkersModal(false);
                      setNotAvailableEmails([]);
                      setWorkerEmailInput("");
                    }}
                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                  >
                    ✕
                  </motion.button>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      {t("settings.emailAddress")}
                    </label>
                    {(() => {
                      const val = workerEmailInput.toLowerCase().trim();
                      if (!val) return null;
                      if (workerEmails.includes(val))
                        return (
                          <p className="text-xs text-red-500 mt-0.5">
                            {t("settings.alreadyInList")}
                          </p>
                        );
                      if (workers.some((w) => w.email?.toLowerCase() === val))
                        return (
                          <p className="text-xs text-red-500 mt-0.5">
                            {t("settings.alreadyOnTeam")}
                          </p>
                        );
                      return null;
                    })()}
                    <div className="flex gap-2">
                      <input
                        type="email"
                        placeholder="worker@example.com"
                        value={workerEmailInput}
                        onChange={(e) => {
                          setWorkerEmailInput(e.target.value.toLowerCase());
                          // Clear server-side errors when user starts typing a new email
                          if (notAvailableEmails.length > 0)
                            setNotAvailableEmails([]);
                        }}
                        className={`flex-1 h-12 px-4 rounded-xl border focus:ring-2 focus:ring-opacity-50 transition-all ${
                          workerEmailInput.trim() &&
                          (workerEmails.includes(
                            workerEmailInput.toLowerCase().trim(),
                          ) ||
                            workers.some(
                              (w) =>
                                w.email?.toLowerCase() ===
                                workerEmailInput.toLowerCase().trim(),
                            ))
                            ? "border-red-400 focus:border-red-400 focus:ring-red-200"
                            : "border-gray-200 focus:border-[#020637] focus:ring-[#020637]/20"
                        }`}
                        onKeyPress={(e) => {
                          if (e.key !== "Enter") return;
                          const val = workerEmailInput.toLowerCase().trim();
                          if (!val) return;
                          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val))
                            return toast.error(t("settings.invalidEmail"));
                          if (workerEmails.includes(val))
                            return toast.error(t("settings.alreadyInList"));
                          if (
                            workers.some((w) => w.email?.toLowerCase() === val)
                          )
                            return toast.error(t("settings.alreadyOnTeam"));
                          setWorkerEmails([...workerEmails, val]);
                          setWorkerEmailInput("");
                        }}
                      />
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="h-12 px-4 bg-[#020637]/10 text-[#020637] rounded-xl font-medium flex items-center gap-2"
                        onClick={() => {
                          const val = workerEmailInput.toLowerCase().trim();
                          if (!val)
                            return toast.error(t("settings.enterEmail"));
                          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val))
                            return toast.error(t("settings.invalidEmail"));
                          if (workerEmails.includes(val))
                            return toast.error(t("settings.alreadyInList"));
                          if (
                            workers.some((w) => w.email?.toLowerCase() === val)
                          )
                            return toast.error(t("settings.alreadyOnTeam"));
                          setWorkerEmails([...workerEmails, val]);
                          setWorkerEmailInput("");
                          toast.success(t("settings.added"));
                        }}
                      >
                        <FiPlus size={18} />
                      </motion.button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        {t("settings.invitedWorkers")} ({workerEmails.length})
                      </span>
                      {workerEmails.length > 0 && (
                        <button
                          onClick={() => setWorkerEmails([])}
                          className="text-sm text-red-500 hover:text-red-700"
                        >
                          {t("settings.clearAll")}
                        </button>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      <AnimatePresence>
                        {notAvailableEmails.length > 0 && (
                          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl mb-2">
                            <span className="text-red-500 text-xs mt-0.5">
                              ⚠
                            </span>
                            <div>
                              <p className="text-xs font-semibold text-red-600 mb-1">
                                {t("settings.emailsNotRegistered")}
                              </p>
                              {notAvailableEmails.map((e) => (
                                <p key={e} className="text-xs text-red-500">
                                  {e}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                        {workerEmails.map((email) => (
                          <motion.div
                            key={email}
                            initial={{ opacity: 1, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 1, x: 20 }}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#020637]/10 flex items-center justify-center">
                                <FiMail className="w-4 h-4 text-[#020637]" />
                              </div>
                              <span className="text-sm font-medium">
                                {email}
                              </span>
                            </div>
                            <button
                              onClick={() =>
                                setWorkerEmails(
                                  workerEmails.filter((e) => e !== email),
                                )
                              }
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <FiTrash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                            </button>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowAddWorkersModal(false);
                      setNotAvailableEmails([]);
                      setWorkerEmailInput("");
                    }}
                    className="flex-1 h-12 px-6 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                    disabled={loadingAddWorkers}
                  >
                    {t("settings.cancel")}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSaveWorkers}
                    disabled={loadingAddWorkers || workerEmails.length === 0}
                    className="flex-1 h-12 px-6 bg-gradient-to-r from-blue-600 to-[#020637] text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loadingAddWorkers ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t("settings.sending")}
                      </>
                    ) : (
                      <>
                        <FiUsers className="w-5 h-5" />
                        {t("settings.sendInvites")}
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </ModalPortal>

      <LanguageModal
        isOpen={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
      />
      <AILanguageModal
        isOpen={showAILanguageModal}
        onClose={() => setShowAILanguageModal(false)}
        initialLanguages={aiLanguages}
      />
      <CustomAlertModal
        visible={alertVisible}
        onClose={() => {
          setAlertVisible(false);
          setModalError("");
        }}
        text={modalError}
        doneText="OK"
        isError={true}
        onSingleButton={true}
      />
      <CustomAlertModal
        visible={timeZoneAlertVisible}
        onClose={() => setTimeZoneAlertVisible(false)}
        text={t("TBio.timezoneMismatch")}
        onCancelPress={() => {
          setTimeZone(initialTimeZone);
        }}
        onDonePress={() => {
          save();
        }}
        doneText={t("TBio.updateTimeZone")}
        cancelText={t("TBio.keepPreviousTimeZone")}
      />
    </motion.div>
  );
}
